import { getTestBySlug } from '@/features/tests/data/tests';
import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapQueryResult, mapTestExecutionDto } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { buildEqFilter, mergeFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  QueryFilter,
  QueryResult,
  QuerySort,
  TestExecutionDto,
  TestExecutionStatusSummaryDto,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const testExecutionSortFieldMap: Readonly<Record<string, string>> = {
  runNumber: 'timestamp',
};

const mapTestExecutionSorts = (sorts: QuerySort[]): QuerySort[] => {
  const mappedSorts: QuerySort[] = [];
  for (const sort of sorts) {
    const field = typeof sort.field === 'string' ? sort.field : String(sort.field);
    const direction = sort.direction === 'desc' ? 'desc' : 'asc';
    const mappedField = Object.prototype.hasOwnProperty.call(testExecutionSortFieldMap, field)
      ? testExecutionSortFieldMap[field]
      : field;
    mappedSorts.push({ field: mappedField, direction });
  }
  return mappedSorts;
};

export const queryTestExecutions = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
  tokenOverride?: string | null,
): Promise<QueryResult<TestExecutionDto>> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters =
    !hasProjectFilter && projectId ? buildEqFilter('projectId', projectId) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts
    ? mapTestExecutionSorts(input.sorts)
    : [{ field: 'timestamp', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<TestExecutionDto>>(
      'test-executions/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapTestExecutionDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryTestExecutionStatusSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
  tokenOverride?: string | null,
): Promise<TestExecutionStatusSummaryDto> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters =
    !hasProjectFilter && projectId ? buildEqFilter('projectId', projectId) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts ? mapTestExecutionSorts(input.sorts) : [];
  try {
    return await apiClient.post<TestExecutionStatusSummaryDto>(
      'test-executions/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getTestExecutionById = async (
  id: string,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<TestExecutionDto> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.get<TestExecutionDto>(`test-executions/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapTestExecutionDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getTestExecutionByRunNumber = async (
  testSlug: string | null,
  runNumber: number | null,
  projectId?: string | null,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<TestExecutionDto | null> => {
  if (!testSlug || runNumber === null || !Number.isFinite(runNumber) || runNumber < 1) {
    return null;
  }
  let test = await getTestBySlug(testSlug, projectId, organisationId, tokenOverride);
  if (!test && projectId) {
    test = await getTestBySlug(testSlug, null, organisationId, tokenOverride);
  }
  if (!test) {
    return null;
  }
  const resolvedProjectId = test.projectId;
  const filters = buildEqFilter('testId', test.id);
  const pageSize = 100;
  const runSorts = [
    { field: 'timestamp', direction: 'asc' },
    { field: 'id', direction: 'asc' },
  ] satisfies QuerySort[];
  const firstPage = await queryTestExecutions(
    resolvedProjectId,
    organisationId,
    {
      page: 1,
      pageSize,
      sorts: runSorts,
      filters,
    },
    tokenOverride,
  );
  const firstMatch = firstPage.items.find((item) => item.runNumber === runNumber);
  if (firstMatch) return firstMatch;
  const maxPage = Math.max(1, Math.ceil(firstPage.total / pageSize));
  if (maxPage === 1) return null;
  const targetPage = Math.min(maxPage, Math.max(2, Math.ceil(runNumber / pageSize)));
  if (targetPage !== 1) {
    const targetResult = await queryTestExecutions(
      resolvedProjectId,
      organisationId,
      {
        page: targetPage,
        pageSize,
        sorts: runSorts,
        filters,
      },
      tokenOverride,
    );
    const targetMatch = targetResult.items.find((item) => item.runNumber === runNumber);
    if (targetMatch) return targetMatch;
  }
  for (let page = 2; page <= maxPage; page += 1) {
    if (page === targetPage) continue;
    const result = await queryTestExecutions(
      resolvedProjectId,
      organisationId,
      {
        page,
        pageSize,
        sorts: runSorts,
        filters,
      },
      tokenOverride,
    );
    const match = result.items.find((item) => item.runNumber === runNumber);
    if (match) return match;
    if (result.items.length < pageSize) break;
  }
  return null;
};

export const createTestExecution = async (
  input: {
    suiteExecutionId: string;
    testId: string;
    status?: TestExecutionDto['status'];
    name?: string | null;
    classname?: string | null;
    file?: string | null;
    durationMs?: number | null;
    failureMessage?: string | null;
    failureType?: string | null;
    failureStack?: string | null;
    systemOut?: string | null;
    systemErr?: string | null;
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<TestExecutionDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    suiteExecutionId: input.suiteExecutionId.trim(),
    testId: input.testId.trim(),
    status: input.status,
    name: input.name ?? undefined,
    classname: input.classname ?? undefined,
    file: input.file ?? undefined,
    durationMs: input.durationMs ?? undefined,
    failureMessage: input.failureMessage ?? undefined,
    failureType: input.failureType ?? undefined,
    failureStack: input.failureStack ?? undefined,
    systemOut: input.systemOut ?? undefined,
    systemErr: input.systemErr ?? undefined,
  };
  try {
    const result = await apiClient.post<TestExecutionDto>('test-executions', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapTestExecutionDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateTestExecution = async (
  input: {
    id: string;
    status?: TestExecutionDto['status'];
    name?: string | null;
    classname?: string | null;
    file?: string | null;
    durationMs?: number | null;
    failureMessage?: string | null;
    failureType?: string | null;
    failureStack?: string | null;
    systemOut?: string | null;
    systemErr?: string | null;
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<TestExecutionDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    status: input.status,
    name: input.name ?? undefined,
    classname: input.classname ?? undefined,
    file: input.file ?? undefined,
    durationMs: input.durationMs ?? undefined,
    failureMessage: input.failureMessage ?? undefined,
    failureType: input.failureType ?? undefined,
    failureStack: input.failureStack ?? undefined,
    systemOut: input.systemOut ?? undefined,
    systemErr: input.systemErr ?? undefined,
  };
  try {
    const result = await apiClient.patch<TestExecutionDto>(`test-executions/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapTestExecutionDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteTestExecution = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`test-executions/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const restoreTestExecution = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`test-executions/${input.id}/restore`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
